import {
  attendeeStatusFilter,
  EventTypes,
  SpeakerSubjectFilter,
  SubjectStatus,
  SubjectType
} from 'constants/index'
import { chunk, every } from 'lodash'
import moment from 'moment-timezone'
import { FirestoreDatabase, storageRef } from 'index'
import firebase from 'firebase/app'
import { extendMoment } from 'moment-range'
import { isEmpty } from 'lodash'

export function isValidEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const truncate = (str, maxLength) => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + '...'
  }
  return str
}

export const caseInsensitiveSort = (array, key) =>
  [...array].sort((a, b) => a[key]?.toLowerCase().localeCompare(b[key]?.toLowerCase()))

export const removeLeadingAndTrailingSpaces = (string) => {
  return string.replace(/^\s+|\s+$/g, '')
}

export const checkLeadingAndTrailingSpaces = (string) => {
  return /^\s|\s$/.test(string)
}

export const parseCsvData = (csvData) => {
  const columns = csvData[0]
  const headers = columns?.filter((s) => s !== '')
  headers.forEach((header, index) => {
    headers[index] = transformToCamelCase(header)
  })
  const data = []
  csvData.slice(1).forEach((line) => {
    const obj = {}
    headers.forEach(async (header, index) => {
      if (line[index]) {
        obj[headers[index]] = line[index].trim()
      }
    })
    !isEmpty(obj) && data.push(obj)
  })
  return data
}

// prettier-ignore
export const transformToCamelCase = (str) => {
  let result = ''
  str.trim().split(' ').forEach((part, index) => {
    if (index > 0) result += part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    else result += part.toLowerCase()
  })
  return result
}

export const getSubjectStatusBasedCount = (subjects, type) => {
  const pending = subjects.filter((e) => e.status === SubjectStatus.INVITE_NOT_SENT)
  const invited = subjects.filter((e) => e.status === SubjectStatus.AWAITING_RESPONSE)
  const inReview = subjects.filter((e) => e.status === SubjectStatus.PENDING_DATA_REVIEW)
  const approved = subjects.filter((e) => e.status === SubjectStatus.APPROVED)
  const awaiting =
    type === SubjectType.SPEAKER
      ? subjects?.filter((e) => e.status === SpeakerSubjectFilter.AWAITING_TALKS)
      : []

  return {
    pending: pending.length,
    invited: invited.length,
    inReview: inReview.length,
    approved: approved.length,
    awaiting: awaiting?.length
  }
}

export const getStatusBasedSubTitle = (status) => {
  if (status === SubjectStatus.APPROVED) {
    return 'Congratulations! Your application has been accepted and approved. If you want to make any changes, please click the button below.'
  } else if (status === SubjectStatus.PENDING_DATA_REVIEW) {
    return 'Your information is submitted successfully. If you want to make any changes, please click the button below.'
  } else {
    return ''
  }
}

export const isValidLink = (link) => {
  const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/
  return urlPattern.test(link)
}

export const returnTimeInTimezone = (timestamp, timezone, fallbackTimezone) => {
  return moment(timestamp).tz(timezone ? timezone : fallbackTimezone)
}

export const areAllValuesZero = (object) => {
  return every(object, (value) => value === 0)
}

export const removeDuplicateEmails = (dataArr, objectKey) => {
  const Emails = dataArr?.reduce(
    (acc, obj) => {
      if (obj[objectKey] && !acc.seenEmails.includes(obj[objectKey])) {
        acc.seenEmails.push(obj[objectKey])
        acc.uniqueEmails.push(obj)
      }
      return acc
    },
    { seenEmails: [], uniqueEmails: [] }
  )

  return Emails.uniqueEmails
}

export const SubjectsNameConcatenation = (firstName, lastName) => {
  return lastName && firstName
    ? `${lastName?.trim()} ${firstName?.trim()}`
    : `${lastName?.trim()}${firstName?.trim()}`
}

export const getUniqueArrayOfStrings = (dataArray, key) => {
  const values = dataArray
    ?.map((item) => item?.[key])
    ?.filter((value) => value !== undefined && value)
  return values.length ? Array.from(new Set(values)) : []
}

export const getVenueTitle = (title, floor, room) => {
  if (title && floor && room) {
    return `${title}, ${floor}, ${room}`
  } else if (title && (floor || room)) {
    return `${title}, ${floor || room}`
  } else if (title && !floor && !room) {
    return title
  }
}

export const uploadImageFirstTime = async (file, path, confId) => {
  try {
    const timestamp = new Date().getTime() + ''
    await storageRef.child(`${confId}/${path}/${timestamp}`).put(file)
    return timestamp
  } catch (err) {
    console.error(err)
  }
}

export const removeUndefinedItemsFromArrayOfString = (arrayOfStrings) => {
  return arrayOfStrings.filter((item) => item !== undefined).filter((item) => item)
}

export const deleteBatchDocuments = async (documentRefs) => {
  try {
    if (documentRefs?.length) {
      const batchSize = 500 // firebase allows us to commit a batch max 500 documents
      const chunks = chunk(documentRefs, batchSize)

      const promises = chunks.map((chunk) => {
        const batch = FirestoreDatabase.batch()
        chunk?.forEach((docRef) => {
          if (docRef instanceof firebase.firestore.DocumentReference) {
            batch.delete(docRef)
          }
        })
        return batch.commit()
      })
      await Promise.all(promises)
    }
  } catch (err) {
    console.error(err)
  }
}

export const getWarningTextBasedOnEventTypesChange = (oldEventType, newEventType) => {
  if (oldEventType === EventTypes.STANDALONE && newEventType !== EventTypes.STANDALONE) {
    return ''
  }
  if (newEventType !== oldEventType && newEventType === EventTypes.STANDALONE) {
    return oldEventType === EventTypes.HAVE_SESSIONS
      ? 'You are about to change event type. This action will delete all the sessions and talks created in this event.'
      : 'You are about to change event type. This action will delete all event talks already created.'
  } else if (oldEventType === EventTypes.HAVE_TALKS && newEventType === EventTypes.HAVE_SESSIONS) {
    return 'You are about to change event type. This action will delete all event talks already created.'
  } else if (oldEventType === EventTypes.HAVE_SESSIONS && newEventType === EventTypes.HAVE_TALKS) {
    return 'You are about to change event type. This action will delete all the sessions and talks created in this event.'
  }
}

export const getWarningTextIfEventPosterSessionChanged = (lastPosterSession, newPosterSession) => {
  const lastEvent = lastPosterSession ? 'Poster session' : 'Abstract session'
  const newEvent = newPosterSession ? 'Poster session' : 'Abstract session'
  const lastNumber = lastPosterSession ? 'poster' : 'abstract'
  return `Changing the event from ${lastEvent} to ${newEvent} will remove ${lastNumber} numbers from all talks within this event. You will need to manually reassign these numbers to each talk afterward.`
}

export const getSubjectFilterList = (subjectType, speakerCanUploadTalks) => {
  if (subjectType === SubjectType.SPEAKER) {
    const statusList = Object.keys(SpeakerSubjectFilter).map((key) => ({
      id: SpeakerSubjectFilter[key],
      value: SpeakerSubjectFilter[key]
    }))
    statusList.unshift({ id: 'All', value: 'All' })

    return speakerCanUploadTalks
      ? statusList
      : statusList.filter((status) => status.id !== 'Awaiting talks')
  } else if (subjectType === SubjectType.ATTENDEE) {
    const statusList = Object.keys(attendeeStatusFilter).map((key) => ({
      id: SpeakerSubjectFilter[key],
      value: SpeakerSubjectFilter[key]
    }))
    statusList.unshift({ id: 'All', value: 'All' })
    return statusList
  } else {
    const statusList = Object.keys(SubjectStatus).map((key) => ({
      id: SubjectStatus[key],
      value: SubjectStatus[key]
    }))
    statusList.unshift({ id: 'All', value: 'All' })
    return statusList
  }
}

export const getDatesInRange = (startDate, endDate) => {
  const MomentRange = extendMoment(moment)
  const start = moment(startDate, 'MM/DD/YYYY')
  const end = moment(endDate, 'MM/DD/YYYY')
  const range = MomentRange.range(start, end)

  return Array.from(range.by('day')).map((m) => m?.format('MM/DD/YYYY'))
}

export const loadImageBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous' // Ensure cross-origin requests are allowed if necessary
    img.src = url
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      const dataURL = canvas.toDataURL('image/png') // Convert image to base64
      resolve(dataURL)
    }
    img.onerror = (err) => reject(err)
  })
}

export const getFormattedFullName = (fullName, credential) => {
  const name = []
  if (fullName) {
    name.push(fullName)
  }
  if (credential && credential.trim()) {
    name.push(credential)
  }
  return name.length ? name.join(', ') : ''
}

export const getFormattedLocation = (city, state) => {
  const location = []
  if (city) {
    location.push(city)
  }
  if (state) {
    location.push(state)
  }

  return location.length ? location.join(', ') : ''
}

export const formatToFourDigits = (num) => {
  return num ? num.toString().padStart(4, '0') : ''
}
